import { useEffect, useState } from 'react';
import { useAuth } from './useAuth';
import { apiClient } from '../services/apiClient';

interface UserWithRoles {
  uuid: string;
  email: string;
  name: string;
  type: string;
  roles: string[];
  feature_flags: string[];
}

/**
 * Custom hook to fetch and manage user roles
 * Returns user roles and loading state
 */
export const useUserRoles = () => {
  const { isAuthenticated, isAuthEnabled } = useAuth();
  const [roles, setRoles] = useState<string[]>([]);
  const [featureFlags, setFeatureFlags] = useState<string[]>([]);
  const [userUuid, setUserUuid] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchUserRoles = async () => {
      // If auth is not enabled or user is not authenticated, return empty roles
      if (!isAuthEnabled || !isAuthenticated) {
        setRoles([]);
        setUserUuid(null);
        setError(null);
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const response = await apiClient.get<UserWithRoles>('/auth/user');
        setRoles(response.roles || []);
        setFeatureFlags(response.feature_flags || []);
        setUserUuid(response.uuid || null);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch user roles:', err);
        setError('Failed to fetch user roles');
        setRoles([]);
        setFeatureFlags([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserRoles();
  }, [isAuthenticated, isAuthEnabled]);

  /**
   * Check if user has any of the specified roles
   */
  const hasAnyRole = (rolesToCheck: string[]): boolean => {
    return rolesToCheck.some(role => roles.includes(role));
  };

  /**
   * Check if user has a specific role
   */
  const hasRole = (role: string): boolean => {
    return roles.includes(role);
  };

  /**
   * Check if user has admin privileges (ORG_ADMIN or SUPER_ADMIN)
   */
  const isAdmin = (): boolean => {
    return hasAnyRole(['ORG_ADMIN', 'SUPER_ADMIN']);
  };

  /**
   * Check if user has the ORG_ADMIN role
   */
  const isOrgAdmin = (): boolean => {
    return hasRole('ORG_ADMIN');
  };

  /**
   * Check if user has super admin privileges
   */
  const isSuperAdmin = (): boolean => {
    return hasRole('SUPER_ADMIN');
  };

  const hasFeatureFlag = (flag: string): boolean => {
    return featureFlags.includes(flag);
  };

  return {
    roles,
    featureFlags,
    userUuid,
    isLoading,
    error,
    hasRole,
    hasAnyRole,
    isAdmin,
    isOrgAdmin,
    isSuperAdmin,
    hasFeatureFlag,
  };
};